import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import performRequest from "../../../network/perform-request";
import { competitionCustomFieldsStoreRoute, competitionCustomFieldsUpdateRoute } from "../../../network/api-routes";

const FIELD_TYPES = [
  { value: "short_text", labelId: "Texto corto" },
  { value: "long_text", labelId: "Texto largo" },
  { value: "single_select", labelId: "Selección única" },
  { value: "boolean", labelId: "Sí / No" },
];

const SCOPES = [
  { value: "registration", labelId: "Por inscripción" },
  { value: "player", labelId: "Por jugador" },
];

const emptyState = {
  label: "",
  type: "short_text",
  scope: "registration",
  required: false,
  placeholder: "",
  help_text: "",
  options: [],
  scope_target: "global", // "global" | "tournament"
  tournament_id: null,
};

const CustomFieldFormModal = ({ open, onClose, onSaved, competitionId, tournaments = [], user, field = null }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!field;

  const [form, setForm] = useState(emptyState);
  const [optionDraft, setOptionDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    if (field) {
      setForm({
        label: field.label || "",
        type: field.type || "short_text",
        scope: field.scope || "registration",
        required: !!field.required,
        placeholder: field.placeholder || "",
        help_text: field.help_text || "",
        options: Array.isArray(field.options) ? field.options : [],
        scope_target: field.tournament_id ? "tournament" : "global",
        tournament_id: field.tournament_id || null,
      });
    } else {
      setForm(emptyState);
    }
    setOptionDraft("");
    setErrors({});
  }, [open, field]);

  const isSingleSelect = form.type === "single_select";

  const tournamentOptions = useMemo(() => tournaments.map((t) => ({ id: t.id, name: t.name })), [tournaments]);

  // An error belongs to the value that caused it; editing the field clears it.
  const clearErrors = (...keys) =>
    setErrors((prev) => {
      if (!keys.some((key) => prev[key])) return prev;
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });

  const handleChange = (key) => (event) => {
    const value = event?.target?.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    clearErrors(key, ...(key === "type" ? ["options"] : []));
  };

  const addOption = () => {
    const value = optionDraft.trim();
    if (!value) return;
    if (form.options.includes(value)) {
      setOptionDraft("");
      return;
    }
    setForm((prev) => ({ ...prev, options: [...prev.options, value] }));
    clearErrors("options");
    setOptionDraft("");
  };

  const removeOption = (value) => {
    setForm((prev) => ({ ...prev, options: prev.options.filter((o) => o !== value) }));
  };

  const validate = () => {
    const next = {};
    if (!form.label.trim()) next.label = intl.formatMessage({ id: "Este campo es obligatorio" });
    if (form.label.length > 150) next.label = intl.formatMessage({ id: "Máximo 150 caracteres" });
    if (isSingleSelect && form.options.length === 0) {
      next.options = intl.formatMessage({ id: "Agrega al menos una opción" });
    }
    if (form.scope_target === "tournament" && !form.tournament_id) {
      next.tournament_id = intl.formatMessage({ id: "Selecciona un torneo" });
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    const payload = {
      competition_id: competitionId,
      tournament_id: form.scope_target === "tournament" ? form.tournament_id : null,
      label: form.label.trim(),
      type: form.type,
      scope: form.scope,
      required: !!form.required,
      placeholder: form.placeholder?.trim() || null,
      help_text: form.help_text?.trim() || null,
      options: isSingleSelect ? form.options : null,
    };

    try {
      if (isEdit) {
        await performRequest("POST", competitionCustomFieldsUpdateRoute, { id: field.id, ...payload }, true, user);
      } else {
        await performRequest("POST", competitionCustomFieldsStoreRoute, payload, true, user);
      }
      enqueueSnackbar(intl.formatMessage({ id: isEdit ? "Campo actualizado" : "Campo creado" }), {
        variant: "success",
      });
      onSaved && onSaved();
    } catch (error) {
      const apiErrors = error?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        const next = {};
        Object.keys(apiErrors).forEach((k) => {
          next[k] = Array.isArray(apiErrors[k]) ? apiErrors[k][0] : apiErrors[k];
        });
        setErrors(next);
      }
      enqueueSnackbar(intl.formatMessage({ id: "No se pudo guardar el campo" }), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {intl.formatMessage({ id: isEdit ? "Editar campo personalizado" : "Nuevo campo personalizado" })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={intl.formatMessage({ id: "Etiqueta" })}
            value={form.label}
            onChange={handleChange("label")}
            error={!!errors.label}
            helperText={errors.label}
            inputProps={{ maxLength: 150 }}
            fullWidth
            required
          />

          <FormControl fullWidth>
            <InputLabel>{intl.formatMessage({ id: "Tipo" })}</InputLabel>
            <Select value={form.type} label={intl.formatMessage({ id: "Tipo" })} onChange={handleChange("type")}>
              {FIELD_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {intl.formatMessage({ id: t.labelId })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>{intl.formatMessage({ id: "Alcance de la respuesta" })}</FormLabel>
            <RadioGroup row value={form.scope} onChange={handleChange("scope")}>
              {SCOPES.map((s) => (
                <FormControlLabel
                  key={s.value}
                  value={s.value}
                  control={<Radio />}
                  label={intl.formatMessage({ id: s.labelId })}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <FormControlLabel
            control={<Switch checked={form.required} onChange={handleChange("required")} />}
            label={intl.formatMessage({ id: "Obligatorio" })}
          />

          {isSingleSelect && (
            <Box>
              <FormLabel>{intl.formatMessage({ id: "Opciones" })}</FormLabel>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <TextField
                  size="small"
                  value={optionDraft}
                  onChange={(e) => setOptionDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addOption();
                    }
                  }}
                  placeholder={intl.formatMessage({ id: "Nueva opción" })}
                  inputProps={{ maxLength: 100 }}
                  fullWidth
                />
                <Button variant="outlined" onClick={addOption} startIcon={<AddCircleOutlineIcon />}>
                  {intl.formatMessage({ id: "Agregar" })}
                </Button>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                {form.options.map((o) => (
                  <Chip key={o} label={o} onDelete={() => removeOption(o)} />
                ))}
              </Stack>
              {errors.options && <FormHelperText error>{errors.options}</FormHelperText>}
            </Box>
          )}

          <TextField
            label={intl.formatMessage({ id: "Placeholder" })}
            value={form.placeholder}
            onChange={handleChange("placeholder")}
            inputProps={{ maxLength: 150 }}
            fullWidth
          />

          <TextField
            label={intl.formatMessage({ id: "Texto de ayuda" })}
            value={form.help_text}
            onChange={handleChange("help_text")}
            inputProps={{ maxLength: 250 }}
            fullWidth
            multiline
            minRows={2}
          />

          <FormControl>
            <FormLabel>{intl.formatMessage({ id: "Aplica a" })}</FormLabel>
            <RadioGroup
              value={form.scope_target}
              onChange={(e) => {
                setForm((prev) => ({
                  ...prev,
                  scope_target: e.target.value,
                  tournament_id: e.target.value === "global" ? null : prev.tournament_id,
                }));
                clearErrors("tournament_id");
              }}
            >
              <FormControlLabel
                value="global"
                control={<Radio />}
                label={intl.formatMessage({ id: "Todos los torneos del evento" })}
              />
              <FormControlLabel
                value="tournament"
                control={<Radio />}
                label={intl.formatMessage({ id: "Solo un torneo específico" })}
              />
            </RadioGroup>
          </FormControl>

          {form.scope_target === "tournament" && (
            <FormControl fullWidth error={!!errors.tournament_id}>
              <InputLabel>{intl.formatMessage({ id: "Torneo" })}</InputLabel>
              <Select
                value={form.tournament_id || ""}
                label={intl.formatMessage({ id: "Torneo" })}
                onChange={handleChange("tournament_id")}
              >
                {tournamentOptions.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.tournament_id && <FormHelperText>{errors.tournament_id}</FormHelperText>}
            </FormControl>
          )}
        </Stack>
        <IconButton onClick={onClose} sx={{ position: "absolute", top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {intl.formatMessage({ id: "Cancelar" })}
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {intl.formatMessage({ id: saving ? "Guardando..." : "Guardar" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomFieldFormModal;
